import { useId } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, TertiaryButton } from '@enechain/ecloud-designsystem'
import {
  FaChevronLeftRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { iconSize } from '~/constants/iconSize'
import {
  civilDateToDate,
  compareCivilDate,
  dateToCivilDate,
} from '~/models/civilDate'
import { addDays, startOfTodayJst } from '~/models/dateTime'

/**
 * 配置バリアント (閉じた variant。 寸法値は公開しない =
 * docs/adr/frontend-skeleton-closed-style-props.md)。
 *
 * - `inline`: 1 行に [前日][日付][翌日][今日]。 フィルタ行 (FilterToolbar) 用の既定
 * - `stacked`: 日付入力を全幅で 1 行目、 2 行目に [前日][今日][翌日]。
 *   幅 260px のフィルタサイドバー用 (design/supply/48data.html の対象日 +
 *   .filter-date-nav と同形。 inline のままでは 260px に収まらない)
 * - `stackedCompact`: `stacked` と同じ 2 段構成で、 段間・ボタン間を詰めた密度。
 *   同時同量のフィルタカード (8080 `balance/bg.html` / `balance/hatsu.html` の
 *   `.filter-date` = gap 4px / `.filter-date-nav` = gap 4px。 幅の狭い日付列) 用。
 *   `stacked` の余白は 260px のサイドバー向けに決めた値で、 狭い日付列にそのまま使うと
 *   8080 より段が離れるため密度だけ分ける (寸法値は公開しない = 閉じた variant のまま)。
 */
type DateStepperLayout = 'inline' | 'stacked' | 'stackedCompact'

type DateStepperProps = {
  value: Date
  onChange: (date: Date) => void
  onTodayClick?: () => void
  todayLabel?: string
  dateAriaLabel?: string
  /**
   * true の場合、末尾の「今日」ボタンを描画しない。
   * demand/forecast-list.html (design 2026-07-15 ユーザー指示) の単日タブは
   * 対象日 radio (当日/翌日/翌々日/翌々日以降) の「当日」が同じ役割を担うため、
   * 重複する「今日」ボタンを design どおり非表示にする用途。省略時は false
   * (= 既存呼び出しは全て表示のまま、後方互換)。
   */
  hideTodayButton?: boolean
  /** 配置バリアント。省略時は `inline`。 */
  layout?: DateStepperLayout
  /**
   * これより後の日付を選択不可にする (issue #6361)。表示日が maxDate 以上のとき
   * 翌日ボタンを disabled にし、日付入力側 (`CompactCivilDateField`) にも伝播する。
   * 省略時は従来どおり無制限 (= 後方互換。既存呼び出しは全て影響を受けない)。
   */
  maxDate?: Date
  /**
   * maxDate 到達時に翌日ボタンの Tooltip へ出す無効理由の文言。
   *
   * `maxDate` 自体は「これより後は選べない」という中立な契約で、*なぜ*選べないか
   * (= policy) は持たない。理由は呼び出し側 (例: 同時同量の `DateNav` が持つ
   * 「未来日は選択できません」) が決めるべきもので、本 component が特定 feature の
   * 文言を決め打ちすると、未来日以外の理由で maxDate を使う呼び出し側に嘘の説明を
   * 出してしまう。省略時はボタン名 (`common:filter.nextDay`) にフォールバックする。
   * `maxDate` 未指定なら参照されない。
   */
  maxDateDisabledReason?: string
  /**
   * true のとき、 前後日 / 日付欄 / 今日を一括で disabled にする (issue #7131:
   * ワークフローケース対象日への固定)。 `maxDate` (未来日ガード、 翌日ボタンのみ)
   * とは独立した契約で、 `disabled` は 3 コントロール全てを常に無効化する
   * (`maxDate` 到達の有無を問わない)。 省略時は従来どおり無制限 (= 後方互換)。
   */
  disabled?: boolean
  /**
   * `disabled` が true のときに 3 コントロールの Tooltip へ出す理由文言。
   * `maxDateDisabledReason` と同じ契約 (省略時はボタン名にフォールバック)。
   * `disabled` が false のときは参照されない。
   */
  disabledReason?: string
}

/** 日付入力・日送りボタンの高さ (design .ecl-date-input--sm / .filter-date-nav = 40px)。 */
const CONTROL_HEIGHT = '40px'

export const DateStepper = ({
  value,
  onChange,
  onTodayClick,
  todayLabel,
  dateAriaLabel,
  hideTodayButton = false,
  layout = 'inline',
  maxDate,
  maxDateDisabledReason,
  disabled = false,
  disabledReason,
}: DateStepperProps): React.ReactElement => {
  const { t } = useTranslation()
  const dateFieldId = useId()

  const isStacked = layout === 'stacked' || layout === 'stackedCompact'
  const isCompact = layout === 'stackedCompact'
  // 暦日同士で比較する (`value` / `maxDate` とも絶対時刻ではなくカレンダー日
  // `Date` の契約だが、時刻成分が万一混入していても暦日比較なら影響しない)。
  const isNextDisabled =
    disabled ||
    (maxDate !== undefined &&
      compareCivilDate(dateToCivilDate(value), dateToCivilDate(maxDate)) >= 0)

  const handlePrev = (): void => {
    // disabled の保険 (通常はボタン側の disabled で押下自体を防ぐ)。
    if (disabled) return
    onChange(addDays(value, -1))
  }

  const handleNext = (): void => {
    // disabled の保険 (通常はボタン側の disabled で押下自体を防ぐ)。
    if (isNextDisabled) return
    onChange(addDays(value, 1))
  }

  const handleToday = (): void => {
    // disabled の保険 (通常はボタン側の disabled で押下自体を防ぐ)。
    if (disabled) return
    if (onTodayClick !== undefined) {
      onTodayClick()
    } else {
      // `value` / `maxDate` はカレンダー日 `Date` の契約 (:92-93 参照)。ここだけ
      // 絶対時刻 `serverNow()` を書き戻すと同じ契約からの部分逸脱になる。
      onChange(startOfTodayJst())
    }
  }

  const barePrevButton = (
    <TertiaryButton
      flex="0 0 auto"
      width={CONTROL_HEIGHT}
      minWidth={CONTROL_HEIGHT}
      height={CONTROL_HEIGHT}
      aria-label={t('common:filter.prevDay')}
      disabled={disabled}
      onClick={handlePrev}
      paddingX={0}
      size="md"
    >
      <FaChevronLeftRegular
        boxSize={iconSize.labeledButton}
        aria-hidden="true"
      />
    </TertiaryButton>
  )

  const bareNextButton = (
    <TertiaryButton
      flex="0 0 auto"
      width={CONTROL_HEIGHT}
      minWidth={CONTROL_HEIGHT}
      height={CONTROL_HEIGHT}
      aria-label={t('common:filter.nextDay')}
      disabled={isNextDisabled}
      onClick={handleNext}
      paddingX={0}
      size="md"
    >
      <FaChevronRightRegular
        boxSize={iconSize.labeledButton}
        aria-hidden="true"
      />
    </TertiaryButton>
  )

  // アイコンだけの前日 / 翌日ボタンは常に DS Tooltip でボタン名を出す (UI 辞書 components/datepicker.md「`aria-label` と `Tooltip` の両方を付ける」)。
  // 押せない時は理由を出す (disabled の間は ButtonTooltip の包みが Tab の止まり先になる)。
  // content はどの状態でも非空なので、 `ButtonTooltip` JSDoc が警告する `isHidden` の
  // 切り替え (= ボタンの remount) は起きない。
  const prevButton = (
    <ButtonTooltip
      content={
        disabled
          ? (disabledReason ?? t('common:filter.prevDay'))
          : t('common:filter.prevDay')
      }
    >
      {barePrevButton}
    </ButtonTooltip>
  )

  const nextButton = (
    <ButtonTooltip
      content={
        disabled
          ? (disabledReason ?? t('common:filter.nextDay'))
          : isNextDisabled
            ? (maxDateDisabledReason ?? t('common:filter.nextDay'))
            : t('common:filter.nextDay')
      }
    >
      {bareNextButton}
    </ButtonTooltip>
  )

  const bareTodayButton = hideTodayButton ? null : (
    // stacked では design の `.filter-date-nav > .btn-tertiary{flex:1 1 auto}` に合わせ、
    // 前日 / 翌日 の間の余白いっぱいに広げる。
    <TertiaryButton
      flex={isStacked ? '1 1 auto' : '0 0 auto'}
      height={CONTROL_HEIGHT}
      disabled={disabled}
      onClick={handleToday}
      paddingX={3}
      size="md"
    >
      {todayLabel ?? t('common:filter.today')}
    </TertiaryButton>
  )

  const todayButton =
    bareTodayButton === null || !disabled ? (
      bareTodayButton
    ) : (
      <ButtonTooltip content={disabledReason ?? t('common:filter.today')}>
        {bareTodayButton}
      </ButtonTooltip>
    )

  // CompactCivilDateField は 40px 固定高 (design .ecl-date-input--sm 準拠) で
  // CONTROL_HEIGHT と一致するため日送りボタンとの高さ揃えは崩れない。
  //
  // accessible name は **`aria-label` で直接与える**。 visually-hidden な
  // `<label htmlFor>` を足すと、 呼び出し側が既に可視ラベルを描いている場合
  // (同時同量の `DateNav` は「表示日」 を `<Text>` で出す) に同じ文言の
  // テキストノードが 2 つになり、 `getByText` が複数マッチする
  // (2026-08-13 に実際に発生)。
  const bareDateInput = (
    <CompactCivilDateField
      aria-label={dateAriaLabel ?? t('common:filter.date')}
      disabled={disabled}
      id={dateFieldId}
      max={maxDate !== undefined ? dateToCivilDate(maxDate) : undefined}
      onChange={(d) => {
        if (d !== undefined) onChange(civilDateToDate(d))
      }}
      value={dateToCivilDate(value)}
    />
  )

  const dateInput = (
    <Box width={isStacked ? '100%' : COMPACT_DATE_FIELD_WIDTH}>
      {!disabled ? (
        bareDateInput
      ) : (
        <ButtonTooltip
          content={disabledReason ?? dateAriaLabel ?? t('common:filter.date')}
        >
          {bareDateInput}
        </ButtonTooltip>
      )}
    </Box>
  )

  if (isStacked) {
    return (
      // 日付入力と日送りナビは design 上それぞれ独立した .filter-group なので、
      // 元はサイドバーのフィルタ間余白 (space-4 = 16px) を段間にも入れていた。
      //
      // `stacked` (48 点データのサイドバー) は **[design 差異]** 12px にしている。
      // design (8080 `supply/48data.html` の
      // `.ecl-toolbar--column.s48-filter-sidebar { gap: var(--space-4) }`) は 16px だが、
      // 実画面では「日付入力とその日送りボタンは 1 つの部品に見えるべきで、 他フィルタ間と
      // 同じ余白だと離れて見える」 というデザイナー判断 (2026-08-12、 preview pr-4973 目視)。
      // design 側を追従させるかは別途確認中。
      //
      // `stackedCompact` (同時同量のフィルタカード) は 8080 の実値どおり 4px。
      // 8080 `css/balance.css` の `.filter-date { gap:4px }` / `.filter-date-nav { gap:4px }`。
      <Flex alignItems="stretch" flexDirection="column" gap={isCompact ? 1 : 3}>
        {dateInput}
        <Flex gap={isCompact ? 1 : 2}>
          {prevButton}
          {todayButton}
          {nextButton}
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex alignItems="center" gap={2}>
      {prevButton}
      {dateInput}
      {nextButton}
      {todayButton}
    </Flex>
  )
}
